import { useEffect, useState } from 'react';
import { Button, Stack } from 'react-bootstrap';
import RangeSlider from 'react-bootstrap-range-slider';
import { useTranslation } from 'react-i18next';

import {
  faVolumeDown,
  faVolumeHigh,
  faVolumeMute,
} from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import useRemoteBoundStore from 'src/store/useRemoteBoundStore';

import { Api } from 'src/models/Api.model';
import CameraConnection from 'src/models/Camera/CameraConnection';
import { I18NextNS } from 'src/models/Language.model';

import { captureError } from 'src/utils/errorReporting';

const MIN_VOL = 0;
const MAX_VOL = 100;

export const VolumeControl = () => {
  const { cameraData, setCameraData } = useRemoteBoundStore();

  const [vol, setVol] = useState<number>(MIN_VOL);
  const [isPending, setIsPending] = useState(false);

  useEffect(() => {
    if (cameraData) {
      setVol(+cameraData.volume);
    }
  }, [cameraData]);

  const {
    t,
    i18n: { language },
  } = useTranslation(I18NextNS.COMMON);

  const handleSetVol = async (vol: number) => {
    try {
      setIsPending(true);

      if (!cameraData) {
        throw new Error(t('message.error.cameraDataNotFound'));
      }

      await CameraConnection.getInstance().setVolume(vol);
      setVol(vol);
      setCameraData({ ...cameraData, volume: '' + vol });
    } catch (error) {
      captureError(error, 'Failed to set volume');

      Api.alertBox({
        title: t('bootbox.title.Error'),
        message: t('message.error.failedToSetVolume'),
        locale: language,
        type: 'error',
      });
    } finally {
      setIsPending(false);
    }
  };

  return (
    <Stack direction="horizontal" gap={1}>
      <Button
        variant="light"
        className="p-0"
        onClick={() => handleSetVol(MIN_VOL)}
        disabled={isPending}
      >
        <FontAwesomeIcon
          icon={vol ? faVolumeDown : faVolumeMute}
          className="mx-1"
        />
      </Button>

      <RangeSlider
        value={vol}
        tooltipLabel={(currentValue) => `${currentValue}%`}
        step={10}
        onChange={(e) => setVol(+e.target.value)}
        onAfterChange={(e) => handleSetVol(+e.target.value)}
        disabled={isPending}
      />

      <Button
        variant="light"
        className="p-0"
        onClick={() => handleSetVol(MAX_VOL)}
        disabled={isPending}
      >
        <FontAwesomeIcon icon={faVolumeHigh} className="mx-1" />
      </Button>
    </Stack>
  );
};
